// The Forecasting tab's Overview (2026-10-29, Patrick's ask; weeks ahead and the service-level Phone basis 2026-11-02). Two tables:
//   1. Are we short overall?  every function's work (Phone, Chat, Email, Support) as FTE on his 23% method,
//                              the Seniors' measured CXHub help, the scheduled FTE of the teams that do customer
//                              work, and the gap before and after that help.
//   2. Who does what          a work matrix: for each team, the Bright Pattern handle time by channel and the
//                              CXHub handle time by account group, beside the CXHub time it was scheduled for.
// All the arithmetic is on the server (server/src/services/forecasting-overview.js); this only lays it out.
// Hours sit behind a toggle, as on the other views.
import { useEffect, useState } from 'react'
import {
  Alert, Box, CircularProgress, FormControlLabel, Paper, Switch, Table, TableBody, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import { fetchForecastOverview } from '../api.js'
import SectionHeading from './SectionHeading.jsx'
import { Cell, Head, WeekCell, Fte, Divider, rowSx } from './ForecastTableParts.jsx'
import { formatGap, gapTone, weekLabel, EM_DASH } from '../forecastPhoneChat.js'
import { pickBasis, defaultBasis, hasServiceLevel, BASIS_SERVICE, BASIS_PLANNING } from '../forecastOverview.js'

const TABLE_BOX = { width: 'fit-content', maxWidth: '100%' }

// Matrix hours per week: whole hours, a dash with no data.
const formatH = (h) => (typeof h === 'number' && Number.isFinite(h) ? `${Math.round(h).toLocaleString('en-US')}` : EM_DASH)

function StaffingTable({ report, showHours, basis }) {
  const a = report.assumptions
  const coming = report.coming ?? []
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Week ending</Head>
            <Head colSpan={5} left>FTE needed (× {1 + a.shrinkageRate})</Head>
            <Head rowSpan={2} left>Seniors' help</Head>
            <Head rowSpan={2} left>FTE scheduled (÷ {a.scheduledHoursPerFte})</Head>
            <Head colSpan={2} left>Gap</Head>
          </TableRow>
          <TableRow>
            <Head left>Phone</Head>
            <Head>Chat</Head>
            <Head>Email</Head>
            <Head>Support</Head>
            <Head>Total</Head>
            <Head left>Before help</Head>
            <Head>After help</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={10}>Complete weeks · oldest first</Divider>
          {report.weeks.map((row) => {
            const { need: n, gap } = pickBasis(row, basis)
            return (
              <TableRow key={row.week} sx={rowSx(row)}>
                <WeekCell row={row} />
                <Cell left muted={!n.phone}><Fte value={n.phone} showHours={showHours} /></Cell>
                <Cell muted={!n.chat}><Fte value={n.chat} showHours={showHours} /></Cell>
                <Cell muted={!n.email}><Fte value={n.email} showHours={showHours} /></Cell>
                <Cell muted={!n.support}><Fte value={n.support} showHours={showHours} /></Cell>
                <Cell bold muted={!n.total}><Fte value={n.total} showHours={showHours} /></Cell>
                <Cell left muted={!n.help}><Fte value={n.help} showHours={showHours} /></Cell>
                <Cell left bold muted={!row.supply}><Fte value={row.supply} showHours={showHours} /></Cell>
                <Cell left bold tone={gapTone(gap.beforeHelp)} muted={gap.beforeHelp == null}>{formatGap(gap.beforeHelp)}</Cell>
                <Cell bold tone={gapTone(gap.afterHelp)} muted={gap.afterHelp == null}>{formatGap(gap.afterHelp)}</Cell>
              </TableRow>
            )
          })}
          {coming.length > 0 && (
            <>
              <Divider colSpan={10}>Coming weeks · forecast, this week in full (schedules run through {report.scheduledThrough ? weekLabel(report.scheduledThrough) : EM_DASH})</Divider>
              {coming.map((row) => {
                const { need: n, gap } = pickBasis(row, basis)
                return (
                  <TableRow key={row.week} sx={rowSx(row)}>
                    <WeekCell row={row} />
                    <Cell left italic muted={!n.phone}><Fte value={n.phone} showHours={showHours} /></Cell>
                    <Cell italic muted={!n.chat}><Fte value={n.chat} showHours={showHours} /></Cell>
                    <Cell italic muted={!n.email}><Fte value={n.email} showHours={showHours} /></Cell>
                    <Cell italic muted={!n.support}><Fte value={n.support} showHours={showHours} /></Cell>
                    <Cell bold italic muted={!n.total}><Fte value={n.total} showHours={showHours} /></Cell>
                    <Cell left italic muted>{EM_DASH}</Cell>
                    <Cell left bold italic muted={!row.supply}>
                      <Fte value={row.supply} showHours={showHours} />
                      {row.lowSchedule && <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }} title="Scheduled hours are well under recent weeks: time off, or shifts not entered yet">†</Typography>}
                    </Cell>
                    <Cell left bold italic tone={gapTone(gap.beforeHelp)} muted={gap.beforeHelp == null}>{formatGap(gap.beforeHelp)}</Cell>
                    <Cell italic muted>{EM_DASH}</Cell>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

function MatrixTable({ matrix }) {
  const t = matrix.totals
  const line = (r, bold = false) => (
    <TableRow key={r.team}>
      <Cell align="left" bold={bold}>{r.team}</Cell>
      <Cell left bold={bold} muted={!r.bpPhone}>{formatH(r.bpPhone)}</Cell>
      <Cell bold={bold} muted={!r.bpChat}>{formatH(r.bpChat)}</Cell>
      <Cell bold={bold} muted={!r.bpEmail}>{formatH(r.bpEmail)}</Cell>
      <Cell left bold={bold} muted={!r.cxhubCore}>{formatH(r.cxhubCore)}</Cell>
      <Cell bold={bold} muted={!r.cxhubSupport}>{formatH(r.cxhubSupport)}</Cell>
      <Cell bold={bold} muted={!r.cxhubHandled}>{formatH(r.cxhubHandled)}</Cell>
      <Cell left bold={bold} muted={!r.cxhubScheduled}>{formatH(r.cxhubScheduled)}</Cell>
      <Cell muted>{formatH(r.bpScheduled)}</Cell>
    </TableRow>
  )
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Team</Head>
            <Head colSpan={3} left>Bright Pattern handle time (raw)</Head>
            <Head colSpan={3} left>CXHub handle time</Head>
            <Head colSpan={2} left>Scheduled</Head>
          </TableRow>
          <TableRow>
            <Head left>Phone</Head>
            <Head>Chat</Head>
            <Head>Email</Head>
            <Head left>1A, AM (eBay, Website)</Head>
            <Head>Support</Head>
            <Head>All handled</Head>
            <Head left>CXHub blocks</Head>
            <Head>Bright Pattern queue</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={9}>
            Hours a week, average of the {matrix.weeks.length} complete weeks ending {weekLabel(matrix.weeks[0])} to {weekLabel(matrix.weeks[matrix.weeks.length - 1])}
          </Divider>
          {matrix.rows.map((r) => line(r))}
          {line({ team: 'All teams', ...t }, true)}
        </TableBody>
      </Table>
    </Paper>
  )
}

// The loaded view, apart from the fetch so it can be rendered from a report alone.
export function ForecastOverviewView({ report }) {
  const [showHours, setShowHours] = useState(false)
  const [basis, setBasis] = useState(() => defaultBasis(report))
  const a = report.assumptions
  const serviceShown = basis === BASIS_SERVICE && hasServiceLevel(report)
  return (
    <Box>
      <SectionHeading variant="subtitle1" sx={{ mb: 1, ...TABLE_BOX }} title="Forecasting: Overview" descriptionMaxWidth={1100}>
        Are we short overall? One line per week: all the work the four views count (Phone, Chat, Email, Support) against the scheduled
        hours of every team that does customer work. Nothing is allocated between functions, so each scheduled hour is counted once and the
        help teams give each other nets out. If the total is balanced, the gaps in the other views are mostly cross-help; if it is short, the
        shortage is real. Complete weeks give what happened. Below them, this week in full and the weeks ahead give the forecast need against
        the schedule as it stands.
      </SectionHeading>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <FormControlLabel
          control={<Switch size="small" checked={showHours} onChange={(e) => setShowHours(e.target.checked)} />}
          label="Show hours under each FTE"
        />
        {hasServiceLevel(report) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Phone need</Typography>
            <ToggleButtonGroup size="small" exclusive value={basis} onChange={(_, v) => { if (v) setBasis(v) }} aria-label="Phone need basis">
              <ToggleButton value={BASIS_SERVICE}>Service level</ToggleButton>
              <ToggleButton value={BASIS_PLANNING}>Planning standard</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>
      {a.supportForecast && (
        <Alert severity="info" sx={{ mb: 1, maxWidth: 1100 }}>
          Support's weeks ahead are the Support tab's forecast: the mean of its last {a.supportForecast.weeks.length} complete weeks' email work with its
          holiday weeks, and its phone work held flat. Seniors' help is not assumed ahead.
        </Alert>
      )}
      {report.warnings.includes('support_held_flat') && a.supportFlat && (
        <Alert severity="info" sx={{ mb: 1, maxWidth: 1100 }}>
          The weeks ahead hold Support flat at {a.supportFlat.fte.toFixed(1)} FTE, the mean of its last {a.supportFlat.weeks.length} complete weeks, because
          the Support forecast could not be built just now. Seniors' help is not assumed ahead.
        </Alert>
      )}
      {report.warnings.includes('schedule_low') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          A † marks a coming week whose scheduled hours are under {Math.round(a.lowScheduleShare * 100)}% of the recent weeks' average ({Math.round(a.baselineSupplyHours)} h):
          time off, or shifts not entered yet. Read its gap with that in mind.
        </Alert>
      )}
      {report.warnings.includes('forward_incomplete') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          A coming week is missing some function's forecast, so it has no total or gap.
        </Alert>
      )}
      {!hasServiceLevel(report) && report.warnings.includes('service_phone_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          The Phone view has no service-level figures just now, so Phone is on the planning standard.
        </Alert>
      )}
      {(report.failures ?? []).length > 0 && (
        <Alert severity="error" sx={{ mb: 1, maxWidth: 1100 }}>
          Some of this page could not be read just now, so it is partly empty. Reload to try again.
          {report.failures.map((f) => (
            <Box key={f.section} component="div">{f.section}: {f.message}</Box>
          ))}
        </Alert>
      )}
      {report.warnings.includes('need_incomplete') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          A week is missing some function's work, so it has no total or gap. The functions that are there are still shown.
        </Alert>
      )}
      {report.warnings.includes('per_rep_unavailable') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          The per-agent handle times could not be read just now, so the Seniors' help and the CXHub columns of the matrix are missing. Reload to try again.
        </Alert>
      )}

      <Box sx={{ mt: 2, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="1. Are we short overall?" descriptionMaxWidth={1100}>
          Need is each view's own work hours (the Phone view's calls and Spanish callbacks, chats, Email, and Support with its phone work), as FTE
          = hours ÷ {a.onQueueHoursPerFte} × {1 + a.shrinkageRate}, your {Math.round(a.shrinkageRate * 100)}% standard with no flat spare.{' '}
          {serviceShown
            ? `Phone is on the service level: the agents Erlang A says hold the ${Math.floor(a.serviceTargets.phone.asaSec / 60)}:${String(a.serviceTargets.phone.asaSec % 60).padStart(2, '0')} answer time and ${Math.round(a.serviceTargets.phone.aba * 100)}% abandon goals each hour (real calls for a past week, the forecast ahead), because calls cannot wait. That is more than the planning standard asks, so a week can read short here that the other basis shows over. Switch to the planning standard to compare.`
            : 'Phone is on the planning standard (calls × handle time).'}{' '}The
          calls to Support's TRQ Inbound Voice queue are in the Phone view's call count and in Support's need, so they come off Phone here and
          stay in Support. Scheduled is the scheduled hours of {a.supplyTeams.join(', ')} ÷ {a.scheduledHoursPerFte} (paid headcount: the schedule
          includes the 0.5 h unpaid lunch). The Seniors are not scheduled supply, because they work CXHub in project and loan state: their help
          is measured from the CXHub contacts they handled (contacts × handle time) and comes off the work before the markup, since unscheduled
          hours carry no shrinkage. Their project and loan time is not used, because it ran about twice what they actually handled. The gap is
          shown before that help, which is the honest staffing signal, and after it, which shows how much of the shortage the Seniors close and
          how exposed the plan is when they are away.
        </SectionHeading>
        <StaffingTable report={report} showHours={showHours} basis={basis} />
      </Box>

      {report.matrix && (
        <Box sx={{ mt: 3, ...TABLE_BOX }}>
          <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="2. Who does what" descriptionMaxWidth={1100}>
            The cross-help, from actuals. Bright Pattern columns are Schedule Hub's raw handle time by the team of the agent who handled the
            contact (a transferred contact counts once per leg, and raw time includes time the agent was not looking at an email, so it runs
            above the in-focus time the views use for the need). CXHub columns are contacts × handle time by agent, each agent in their team by
            name: 1A and AM eBay and Website email apart from Support email. Scheduled is the team's CXHub blocks and its Bright Pattern queue
            time, per week. Compare "All handled" with "CXHub blocks" to see how much of the team's CXHub time went to work that is not its
            own, and how much was done outside the schedule (the Seniors have no blocks). Management and anyone the roster does not list are
            shown apart so the totals add up.
          </SectionHeading>
          {report.warnings.includes('bp_team_missing') && (
            <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>Schedule Hub returned no Bright Pattern handle time by team, so those columns are empty.</Alert>
          )}
          <MatrixTable matrix={report.matrix} />
        </Box>
      )}

      <SectionHeading variant="subtitle2" appearance="quiet" sx={{ mt: 2 }} title="Colors and notes" descriptionMaxWidth={1100}>
        Colors: gap red below 0, amber under +1. "—" means no data, never zero. Per-agent numbers are claimed time, not effort, and are not a
        performance measure; they are grouped by team here, never shown by person.
      </SectionHeading>
    </Box>
  )
}

export default function ForecastOverview() {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    fetchForecastOverview()
      .then((r) => { if (live) setReport(r) })
      .catch((e) => { if (live) setError(e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>
  if (error) return <Alert severity="error">{error}</Alert>
  if (!report) return null
  return <ForecastOverviewView report={report} />
}
